import React, { useEffect, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import {
  getAdvertisements,
  createAdvertisement,
  updateAdvertisement,
  uploadAdvertisementImage,
  uploadAdvertisementVideo,
  deleteAdvertisement,
  getStoreOptions,
} from "../../api/adminService";

function toDateInputValue(value) {
  if (!value) return "";
  return value.slice(0, 10);
}

export default function Advertisements() {
  const [ads, setAds] = useState([]);
  const [stores, setStores] = useState([]);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null); // { advertisement? }

  async function reload() {
    const data = await getAdvertisements();
    setAds(data);
  }

  useEffect(() => {
    reload().catch(() => setError("Could not load advertisements. Is the API running?"));
    getStoreOptions()
      .then(setStores)
      .catch(() => setError("Could not load stores."));
  }, []);

  function openCreateDialog() {
    setDialog({ advertisement: null });
  }

  function openEditDialog(advertisement) {
    setDialog({ advertisement });
  }

  async function handleSave(values, mediaType, imageFile, videoFile) {
    const { advertisement } = dialog;
    const payload = {
      title: values.title,
      startDate: values.startDate,
      endDate: values.endDate,
      storeId: values.storeId,
      websiteLinkUrl: values.websiteLinkUrl || null,
      mobileAppLinkUrl: values.mobileAppLinkUrl || null,
      displayOrder: Number(values.displayOrder) || 0,
      isActive: values.isActive,
    };

    try {
      let saved;
      if (advertisement) {
        saved = await updateAdvertisement(advertisement.advertisementId, payload);
      } else {
        saved = await createAdvertisement(payload);
      }

      if (mediaType === "image" && imageFile) {
        await uploadAdvertisementImage(saved.advertisementId, imageFile);
      } else if (mediaType === "video" && videoFile) {
        await uploadAdvertisementVideo(saved.advertisementId, videoFile);
      }

      setDialog(null);
      await reload();
    } catch (err) {
      setError(err.response?.data || "Could not save this advertisement.");
    }
  }

  async function handleDelete(advertisement) {
    if (!window.confirm(`Delete "${advertisement.title}"? This cannot be undone.`)) return;
    try {
      await deleteAdvertisement(advertisement.advertisementId);
      await reload();
    } catch (err) {
      setError(err.response?.data || "This advertisement could not be deleted.");
    }
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4">Advertisements</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
          New Advertisement
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {typeof error === "string" ? error : "Something went wrong."}
        </Alert>
      )}

      <List sx={{ bgcolor: "background.paper", borderRadius: 1 }}>
        {ads.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No advertisements yet.
          </Typography>
        )}
        {ads.map((ad, index) => (
          <React.Fragment key={ad.advertisementId}>
            {index > 0 && <Divider component="li" />}
            <ListItem
              secondaryAction={
                <Stack direction="row" spacing={0.5}>
                  <IconButton size="small" onClick={() => openEditDialog(ad)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(ad)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              }
            >
              <ListItemAvatar>
                <Avatar variant="rounded" src={ad.imageUrl || undefined} sx={{ width: 56, height: 56, mr: 1 }}>
                  {ad.videoUrl ? <VideocamOutlinedIcon /> : <ImageOutlinedIcon />}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={ad.title}
                secondary={
                  `${ad.storeName} · ${toDateInputValue(ad.startDate)} – ${toDateInputValue(ad.endDate)}` +
                  ` · ${ad.viewsCount} views · ${ad.clicksCount} clicks (${ad.clickThroughRate}% CTR)` +
                  (ad.videoUrl ? " · Video" : "") +
                  (ad.isActive ? "" : " · Inactive")
                }
              />
            </ListItem>
          </React.Fragment>
        ))}
      </List>

      {dialog && (
        <AdvertisementDialog
          dialog={dialog}
          stores={stores}
          onClose={() => setDialog(null)}
          onSave={handleSave}
        />
      )}
    </Box>
  );
}

function AdvertisementDialog({ dialog, stores, onClose, onSave }) {
  const { advertisement } = dialog;
  const [title, setTitle] = useState(advertisement?.title || "");
  const [storeId, setStoreId] = useState(advertisement?.storeId || "");
  const [startDate, setStartDate] = useState(toDateInputValue(advertisement?.startDate));
  const [endDate, setEndDate] = useState(toDateInputValue(advertisement?.endDate));
  const [websiteLinkUrl, setWebsiteLinkUrl] = useState(advertisement?.websiteLinkUrl || "");
  const [mobileAppLinkUrl, setMobileAppLinkUrl] = useState(advertisement?.mobileAppLinkUrl || "");
  const [displayOrder, setDisplayOrder] = useState(advertisement?.displayOrder ?? 0);
  const [isActive, setIsActive] = useState(advertisement?.isActive ?? true);
  const [mediaType, setMediaType] = useState(advertisement?.videoUrl ? "video" : "image");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(advertisement?.imageUrl || "");
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(advertisement?.videoUrl || "");

  function handleMediaTypeChange(e, value) {
    if (value) setMediaType(value);
  }

  function handleImageChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  }

  function handleVideoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));
  }

  const canSave = title.trim() && storeId && startDate && endDate;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{advertisement ? "Edit Advertisement" : "New Advertisement"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <ToggleButtonGroup
            value={mediaType}
            exclusive
            onChange={handleMediaTypeChange}
            size="small"
            fullWidth
          >
            <ToggleButton value="image">
              <ImageOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
              Image
            </ToggleButton>
            <ToggleButton value="video">
              <VideocamOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
              Video
            </ToggleButton>
          </ToggleButtonGroup>

          {mediaType === "image" ? (
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar variant="rounded" src={imagePreview || undefined} sx={{ width: 72, height: 72 }}>
                <ImageOutlinedIcon />
              </Avatar>
              <Button component="label" variant="outlined" size="small">
                {imagePreview ? "Change Image" : "Upload Image"}
                <input type="file" accept="image/*" hidden onChange={handleImageChange} />
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={2} alignItems="center">
              {videoPreview ? (
                <video
                  src={videoPreview}
                  muted
                  style={{ width: 96, height: 72, borderRadius: 4, background: "#000", objectFit: "cover" }}
                />
              ) : (
                <Avatar variant="rounded" sx={{ width: 72, height: 72 }}>
                  <VideocamOutlinedIcon />
                </Avatar>
              )}
              <Button component="label" variant="outlined" size="small">
                {videoPreview ? "Change Video" : "Upload Video"}
                <input type="file" accept="video/mp4,video/quicktime" hidden onChange={handleVideoChange} />
              </Button>
            </Stack>
          )}

          <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth autoFocus />

          <TextField
            select
            label="Store"
            value={storeId}
            onChange={(e) => setStoreId(e.target.value)}
            fullWidth
          >
            {stores.map((store) => (
              <MenuItem key={store.storeId} value={store.storeId}>
                {store.storeName}
              </MenuItem>
            ))}
          </TextField>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>

          <TextField
            label="Website Link URL"
            value={websiteLinkUrl}
            onChange={(e) => setWebsiteLinkUrl(e.target.value)}
            fullWidth
          />
          <TextField
            label="Mobile App Link URL"
            value={mobileAppLinkUrl}
            onChange={(e) => setMobileAppLinkUrl(e.target.value)}
            fullWidth
          />
          <TextField
            label="Display Order"
            type="number"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(e.target.value)}
            fullWidth
          />

          {advertisement && (
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="body2">Active</Typography>
              <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSave}
          onClick={() =>
            onSave(
              {
                title: title.trim(),
                storeId,
                startDate,
                endDate,
                websiteLinkUrl,
                mobileAppLinkUrl,
                displayOrder,
                isActive,
              },
              mediaType,
              imageFile,
              videoFile
            )
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
